import { useEffect, useRef, useState } from 'react';
import {
  fetchCities,
  fetchOwner,
  fetchProperties,
  fetchPropertyTypes,
  peekOwner,
  peekProperties,
} from '../services/queries';
import { SITE } from '../config/site';

/** Pass `null` to skip fetching until the filters are known. */
export function useProperties(filters) {
  const key = JSON.stringify(filters);
  const [state, setState] = useState(() => {
    const cachedData = filters ? peekProperties(filters) : undefined;
    return { data: cachedData || [], loading: !cachedData, error: false, ready: Boolean(cachedData) };
  });
  const requestRef = useRef(0);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const params = JSON.parse(key);
    if (!params) {
      return;
    }
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const cachedData = peekProperties(params);
    if (cachedData) {
      setState({ data: cachedData, loading: false, error: false, ready: true });
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: false }));

    fetchProperties(params)
      .then((data) => {
        if (requestRef.current === requestId) {
          setState({ data, loading: false, error: false, ready: true });
        }
      })
      .catch(() => {
        if (requestRef.current === requestId) {
          setState((prev) => ({ ...prev, loading: false, error: true, ready: true }));
        }
      });
  }, [key, attempt]);

  return { ...state, reload: () => setAttempt((value) => value + 1) };
}

export function useOwnerContact() {
  const [owner, setOwner] = useState(() => peekOwner() || SITE.fallbackContact);

  useEffect(() => {
    let active = true;
    fetchOwner().then((data) => {
      if (active) {
        setOwner(data);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  return owner;
}

export function useMetaOptions() {
  const [options, setOptions] = useState({ cities: [], types: [], ready: false });

  useEffect(() => {
    let active = true;
    Promise.all([fetchCities().catch(() => []), fetchPropertyTypes().catch(() => [])])
      .then(([cities, types]) => {
        if (active) {
          setOptions({ cities, types, ready: true });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  return options;
}

export function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}
